'use client';

/**
 * Cheap-Models Testing Mode — global banner.
 *
 * Stays visible while testing mode is on (so it can't be left on unnoticed), but
 * unobtrusive: it flashes the full warning briefly when it first appears, then
 * collapses to a thin pulsing line across the top. Hovering the line slides the
 * full warning back out; moving away collapses it again. Self-hiding (returns
 * null when inactive). Polls the single source of truth (GET /api/testing-mode).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Zap, X } from 'lucide-react';
import { Banner, BANNER_ORDER } from '@/components/shell/banner-stack/BannerStack';

interface TestingModeState {
  active: boolean;
  mode: 'cheap' | 'priority' | 'simulated' | null;
  expiresAt: string | null;
  reason: 'manual' | 'test_account' | null;
  envAllowed?: boolean;
}

const POLL_MS = 60_000;
const FLASH_MS = 4500; // how long the full warning shows before collapsing to a line

function formatRemaining(expiresAt: string | null): string {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expiring…';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `expires in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return `expires in ${hrs}h${rem ? ` ${rem}m` : ''}`;
}

export default function TestingModeBanner() {
  const [state, setState] = useState<TestingModeState | null>(null);
  const [busy, setBusy] = useState(false);
  const [flashing, setFlashing] = useState(false);
  const [hovering, setHovering] = useState(false);
  const wasActiveRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/testing-mode', { cache: 'no-store' });
      if (!res.ok) return;
      setState((await res.json()) as TestingModeState);
    } catch {
      /* leave prior state; never throw from a banner */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  // Flash the full warning once whenever testing mode transitions OFF -> ON
  // (including the first load where it's already on), then collapse to a line.
  useEffect(() => {
    const active = !!state?.active;
    if (active && !wasActiveRef.current) {
      wasActiveRef.current = true;
      setFlashing(true);
      const t = setTimeout(() => setFlashing(false), FLASH_MS);
      return () => clearTimeout(t);
    }
    if (!active) wasActiveRef.current = false;
  }, [state?.active]);

  const turnOff = useCallback(async () => {
    setBusy(true);
    try {
      await fetch('/api/testing-mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: null }),
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  if (!state?.active) return null;

  const expanded = flashing || hovering;
  const isManual = state.reason === 'manual';
  const remaining = formatRemaining(state.expiresAt);
  const isPriority = state.mode === 'priority';

  // Cheap = red quality WARNING; Priority = indigo cost/speed NOTICE;
  // Simulated = amber: no real AI at all (the call simulator's fixture model).
  const theme = state.mode === 'simulated'
    ? {
        bar: 'bg-amber-600 shadow-[0_2px_12px_rgba(217,119,6,0.5)]',
        label: 'Simulated mode is on',
        Icon: AlertTriangle,
        text: 'SIMULATED MODE IS ON — AI responses on your calls are recorded fixtures, not real analysis. For load testing only.',
      }
    : isPriority
    ? {
        bar: 'bg-indigo-600 shadow-[0_2px_12px_rgba(79,70,229,0.5)]',
        label: 'Priority mode is on',
        Icon: Zap,
        text: "PRIORITY MODE IS ON — AI requests use OpenAI's priority tier for lower latency at higher cost (~2×). Models are unchanged.",
      }
    : {
        bar: 'bg-rose-600 shadow-[0_2px_12px_rgba(225,29,72,0.5)]',
        label: 'Cheap-Models mode is on',
        Icon: AlertTriangle,
        text: 'CHEAP-MODELS MODE IS ON — every AI is using the cheapest models. Results are NOT representative of real quality.',
      };
  const BannerIcon = theme.Icon;

  return (
    <Banner
      role="status"
      order={BANNER_ORDER.testingMode}
      className="flex flex-col items-stretch"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      testId="testing-mode-banner"
      attrs={{
        'data-mode': state.mode ?? 'off',
        'data-expanded': expanded ? 'true' : 'false',
        'aria-label': theme.label,
      }}
    >
      {/* Single bar whose height animates between a thin line and the full warning. */}
      <div
        className={`overflow-hidden text-white ${theme.bar}
                    transition-[max-height] duration-300 ease-in-out
                    ${expanded ? 'max-h-16' : 'max-h-[5px]'}
                    ${!expanded || flashing ? 'animate-pulse' : ''}`}
      >
        <div
          className={`flex items-center justify-center gap-2 px-3 py-1.5
                      text-xs sm:text-sm font-semibold transition-opacity
                      ${expanded ? 'opacity-100 duration-300 delay-75' : 'opacity-0 duration-100'}`}
        >
          <BannerIcon className="w-4 h-4 shrink-0" />
          <span className="text-center">
            {theme.text}
            {isManual && remaining ? ` (${remaining})` : ''}
            {state.reason === 'test_account' ? ' (test account default)' : ''}
          </span>
          {isManual && (
            <button
              type="button"
              onClick={turnOff}
              disabled={busy}
              className="ml-2 inline-flex items-center gap-1 rounded bg-white/20 hover:bg-white/30
                         px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide
                         disabled:opacity-50 transition"
              data-testid="testing-mode-banner-off"
            >
              <X className="w-3 h-3" />
              Turn off
            </button>
          )}
        </div>
      </div>
      {/* Transparent strip below the collapsed line to make it easy to hover. */}
      {!expanded && <div className="h-2 w-full" aria-hidden="true" />}
    </Banner>
  );
}
